import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type React from 'react';
import { fetchWithAuth } from '../lib/api';
import { StudioContent } from './StudioContent';
import { StudioSidebar } from './StudioSidebar';
import { StudioChatPanel } from './StudioChatPanel';
import { CoverSection } from './CoverSection';
import { MobileTabBar } from './MobileTabBar';
import type { MobileTab } from './MobileTabBar';

interface ChapterMeta {
  chapterIndex: number;
  areaId: string;
  title: string;
  sectionId?: string;
}

export interface SelectionInfo {
  paraId: string;
  text: string;
  areaId: string;
  chapterLabel: string;
}

interface BookStudioProps {
  bookHtml: string;
  restUrl: string;
  onSave: (newHtml: string) => void;
  /** Called after TOC/Index generation completes to reload book content from the server. */
  onRefreshBook?: () => void;
  /** Ref populated by BookStudio so callers (Book.tsx) can surface 409 lock errors inline. */
  showSectionLockToastRef?: React.MutableRefObject<((sectionId: string, message: string) => void) | null>;
  bookTitle?: string;
  authorName?: string;
  /** sectionId → locked, from the structured book document (book.json). */
  sectionLocks?: Record<string, boolean>;
  /** Starts a single-chapter regeneration job (optional author instruction). */
  onRegenerateChapter?: (sectionId: string, instruction: string) => void;
}

const SECTION_TOGGLES = [
  { type: 'dedication-page', label: 'Dedication' },
  { type: 'toc-page',        label: 'Contents'   },
  { type: 'index-page',      label: 'Index'      },
] as const;

type ToggleSection = typeof SECTION_TOGGLES[number]['type'];

// Front/back matter sections backed by the structured book.json API (Phase 69)
// chapterIndex matches the sentinel values used in StudioContent/StudioSidebar
const MATTER_TOGGLES = [
  { type: 'title-page',  label: 'Title Page',   chapterIndex: -3 },
  { type: 'dedication',  label: 'Dedication',    chapterIndex: -2 },
  { type: 'back-matter', label: 'About Author',  chapterIndex: 998 },
] as const;

const BLANK_DEDICATION =
  '<section class="dedication-page"><p class="dedication-text">Enter your dedication here…</p></section>';

export function BookStudio({ bookHtml, restUrl, onSave, onRefreshBook, showSectionLockToastRef, bookTitle = '', authorName = '', sectionLocks, onRegenerateChapter }: BookStudioProps) {
  const [selection, setSelection] = useState<SelectionInfo | null>(null);
  const [chapters, setChapters] = useState<ChapterMeta[]>([]);
  const [lockMap, setLockMap] = useState<Record<string, boolean>>({});
  const [activeSidebarSection, setActiveSidebarSection] = useState<'cover' | null>(null);
  const [coverKey, setCoverKey] = useState<string | null>(null);

  // TOC/Index generation state (Phase 71)
  const [tocGenerating, setTocGenerating] = useState(false);
  const [indexGenerating, setIndexGenerating] = useState(false);
  const [tocStale, setTocStale] = useState(false);
  const [indexStale, setIndexStale] = useState(false);
  const tocPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const indexPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (tocPollRef.current) clearInterval(tocPollRef.current);
      if (indexPollRef.current) clearInterval(indexPollRef.current);
    };
  }, []);

  const [activeMobileTab, setActiveMobileTab] = useState<MobileTab>('read');
  const acceptRewriteRef = useRef<((paraId: string, newText: string) => Promise<void>) | null>(null);
  // Ref populated by StudioSidebar so callers can trigger per-chapter 409 toasts
  const internalShowSectionLockToastRef = useRef<((sectionId: string, message: string) => void) | null>(null);

  // Expose a stable delegate to the caller via showSectionLockToastRef.
  // Set once on mount; the delegate always reads through internalShowSectionLockToastRef.current
  // so it stays correct even after StudioSidebar populates it.
  useEffect(() => {
    if (!showSectionLockToastRef) return;
    showSectionLockToastRef.current = (sectionId: string, message: string) => {
      internalShowSectionLockToastRef.current?.(sectionId, message);
    };
    return () => { if (showSectionLockToastRef) showSectionLockToastRef.current = null; };
  // showSectionLockToastRef is a ref object — its identity is stable, so empty deps are correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive which sections exist and whether they're visible from the HTML
  const sectionState = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(bookHtml, 'text/html');
    const result = {} as Record<ToggleSection, { exists: boolean; visible: boolean }>;
    for (const { type } of SECTION_TOGGLES) {
      const el = doc.querySelector(`section.${type}`);
      result[type] = { exists: !!el, visible: !!el && el.getAttribute('data-excluded') !== 'true' };
    }
    return result;
  }, [bookHtml]);

  // Derive visibility for structured matter sections by sectionId (Phase 69)
  const matterSectionVisible = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(bookHtml, 'text/html');
    const result: Record<string, boolean> = {};
    for (const { chapterIndex } of MATTER_TOGGLES) {
      const entry = chapters.find(c => c.chapterIndex === chapterIndex && c.areaId === '');
      if (!entry?.sectionId) continue;
      const el = doc.querySelector<HTMLElement>(`[data-section-id="${entry.sectionId}"]`);
      result[entry.sectionId] = !el || el.getAttribute('data-excluded') !== 'true';
    }
    return result;
  }, [bookHtml, chapters]);

  const toggleMatterSection = useCallback(async (chapterIndex: number, show: boolean) => {
    const entry = chapters.find(c => c.chapterIndex === chapterIndex && c.areaId === '');
    const sectionId = entry?.sectionId;
    if (!sectionId) {
      // Fall back to DOM-manipulation if sectionId not yet available (book not migrated)
      return;
    }
    // Optimistic DOM update
    const parser = new DOMParser();
    const doc = parser.parseFromString(bookHtml, 'text/html');
    const sectionEl = doc.querySelector<HTMLElement>(`[data-section-id="${sectionId}"]`);
    if (sectionEl) {
      if (show) sectionEl.removeAttribute('data-excluded');
      else sectionEl.setAttribute('data-excluded', 'true');
      onSave(doc.body.innerHTML);
    }
    try {
      const res = await fetchWithAuth(`${restUrl}book/section/${sectionId}/include`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include: show }),
      });
      if (!res.ok) throw new Error('Toggle failed');
    } catch {
      // Revert optimistic update
      onSave(bookHtml);
    }
  }, [chapters, bookHtml, restUrl, onSave]);

  const toggleSection = useCallback(async (type: ToggleSection, show: boolean) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(bookHtml, 'text/html');
    let el = doc.querySelector<HTMLElement>(`section.${type}`);

    if (show && !el && type === 'dedication-page') {
      // Inject blank dedication after title-page (or at top of body)
      const temp = doc.createElement('div');
      temp.innerHTML = BLANK_DEDICATION;
      const newSection = temp.firstElementChild!;
      const titlePage = doc.querySelector('section.title-page');
      if (titlePage?.nextSibling) {
        doc.body.insertBefore(newSection, titlePage.nextSibling);
      } else {
        doc.body.prepend(newSection);
      }
      el = doc.querySelector<HTMLElement>(`section.${type}`);
    }

    if (!el) return;
    if (show) el.removeAttribute('data-excluded');
    else el.setAttribute('data-excluded', 'true');

    const updatedHtml = doc.body.innerHTML;
    onSave(updatedHtml);
    try {
      const res = await fetchWithAuth(`${restUrl}book/html`, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/html; charset=utf-8' },
        body: updatedHtml,
      });
      if (!res.ok) throw new Error('Save failed');
    } catch {
      onSave(bookHtml);
    }
  }, [bookHtml, restUrl, onSave]);

  const handleGenerateToc = useCallback(async () => {
    if (tocGenerating) return;
    setTocGenerating(true);
    setTocStale(false);
    if (tocPollRef.current) clearInterval(tocPollRef.current);
    try {
      const res = await fetchWithAuth(`${restUrl}book/generate-toc`, { method: 'POST' });
      if (!res.ok) { setTocGenerating(false); return; }
      const { jobId } = await res.json() as { jobId: string };
      tocPollRef.current = setInterval(() => {
        void fetchWithAuth(`${restUrl}job-status/${encodeURIComponent(jobId)}`).then(async r => {
          if (!r.ok) return;
          const data = await r.json() as { status: string };
          if (data.status === 'complete' || data.status === 'error') {
            if (tocPollRef.current) clearInterval(tocPollRef.current);
            setTocGenerating(false);
            if (data.status === 'complete') onRefreshBook?.();
          }
        }).catch(() => {});
      }, 3000);
    } catch {
      setTocGenerating(false);
    }
  }, [restUrl, tocGenerating, onRefreshBook]);

  const handleGenerateIndex = useCallback(async () => {
    if (indexGenerating) return;
    setIndexGenerating(true);
    setIndexStale(false);
    if (indexPollRef.current) clearInterval(indexPollRef.current);
    try {
      const res = await fetchWithAuth(`${restUrl}book/generate-index`, { method: 'POST' });
      if (!res.ok) { setIndexGenerating(false); return; }
      const { jobId } = await res.json() as { jobId: string };
      indexPollRef.current = setInterval(() => {
        void fetchWithAuth(`${restUrl}job-status/${encodeURIComponent(jobId)}`).then(async r => {
          if (!r.ok) return;
          const data = await r.json() as { status: string };
          if (data.status === 'complete' || data.status === 'error') {
            if (indexPollRef.current) clearInterval(indexPollRef.current);
            setIndexGenerating(false);
            if (data.status === 'complete') onRefreshBook?.();
          }
        }).catch(() => {});
      }, 3000);
    } catch {
      setIndexGenerating(false);
    }
  }, [restUrl, indexGenerating, onRefreshBook]);

  const handleChapterBodySaved = useCallback(() => {
    setTocStale(true);
    setIndexStale(true);
  }, []);

  const handleSelectionChange = useCallback((info: SelectionInfo | null) => {
    setSelection(info);
    if (info && window.innerWidth < 768) setActiveMobileTab('chat');
  }, []);

  const handleAccept = useCallback(async (paraId: string, newText: string) => {
    await acceptRewriteRef.current?.(paraId, newText);
  }, []);

  const handleReorderChapters = useCallback(async (newOrder: ChapterMeta[]) => {
    const prevChapters = chapters;
    const prevHtml = bookHtml;

    // Optimistically update chapters state. Preserve front/back matter at fixed
    // positions; re-index real chapters so sidebar IDs stay in sync with DOM IDs.
    const frontMatter = chapters.filter(c => c.areaId === '' && c.chapterIndex < 0);
    const backMatter  = chapters.filter(c => c.areaId === '' && c.chapterIndex >= 999);
    const reindexed   = newOrder.map((ch, i) => ({ ...ch, chapterIndex: i }));
    setChapters([...frontMatter, ...reindexed, ...backMatter]);

    // Parse and reorder chapter sections in the HTML.
    const parser = new DOMParser();
    const doc = parser.parseFromString(bookHtml, 'text/html');
    const body = doc.body;

    // Find all chapter headings (h2[id^="chapter-"]).
    const headings = Array.from(body.querySelectorAll<HTMLElement>('h2[id^="chapter-"]'));

    // Walk up to find the direct-body-child ancestor of each h2 (the block start).
    const blockStartFor = (h2: HTMLElement): HTMLElement => {
      let node: HTMLElement = h2;
      while (node.parentElement && node.parentElement !== body) {
        node = node.parentElement as HTMLElement;
      }
      return node;
    };

    const chapterStarts = headings.map(h2 => {
      const idAttr = h2.getAttribute('id') ?? '';
      const m = idAttr.match(/^chapter-(\d+)$/);
      const chapterIndex = m ? Number(m[1]) - 1 : -1; // 1-based id → 0-based chapterIndex
      return { h2, startNode: blockStartFor(h2), chapterIndex };
    });

    // Collect the sibling run for each chapter block.
    const indexSection = body.querySelector<HTMLElement>('section.index-page');
    const blocks = new Map<number, Node[]>();

    for (let i = 0; i < chapterStarts.length; i++) {
      const { startNode, chapterIndex } = chapterStarts[i];
      const stopNode: Node | null = (i + 1 < chapterStarts.length)
        ? chapterStarts[i + 1].startNode
        : indexSection;

      const nodes: Node[] = [];
      let cursor: Node | null = startNode;
      while (cursor && cursor !== stopNode) {
        const next: Node | null = cursor.nextSibling;
        nodes.push(cursor);
        cursor = next;
      }
      blocks.set(chapterIndex, nodes);
    }

    // Detach all chapter blocks.
    blocks.forEach(nodes => {
      nodes.forEach(n => { if (n.parentNode === body) body.removeChild(n); });
    });

    // Re-attach in newOrder before the index section (or at end of body).
    const insertBefore: Node | null = indexSection ?? null;
    for (const ch of newOrder) {
      const nodes = blocks.get(ch.chapterIndex);
      if (!nodes) continue;
      nodes.forEach(n => {
        if (insertBefore) body.insertBefore(n, insertBefore);
        else body.appendChild(n);
      });
    }

    const updatedHtml = body.innerHTML;
    onSave(updatedHtml);

    try {
      const res = await fetchWithAuth(`${restUrl}book/html`, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/html; charset=utf-8' },
        body: updatedHtml,
      });
      if (!res.ok) throw new Error(`Reorder save failed: ${res.status}`);
    } catch {
      setChapters(prevChapters);
      onSave(prevHtml);
    }
  }, [chapters, bookHtml, restUrl, onSave]);

  return (
    <div
      className="h-full bg-stone-50 flex flex-col md:[display:grid] md:[grid-template-columns:240px_1fr_360px]"
    >
      {/* Left pane — Chapters tab on mobile, always visible on desktop */}
      <aside
        id="studio-sidebar-pane"
        role="tabpanel"
        aria-labelledby="tab-chapters"
        className={`bg-white border-r border-stone-200 overflow-y-auto ${activeMobileTab === 'chapters' ? 'flex flex-col flex-1' : 'hidden'} md:flex md:flex-col`}
        aria-label="Chapter navigation"
      >
        <StudioSidebar
          chapters={chapters}
          lockMap={lockMap}
          sectionLocks={sectionLocks}
          onRegenerateChapter={onRegenerateChapter}
          restUrl={restUrl}
          onLockMapChange={setLockMap}
          contentPaneId="studio-content-pane"
          onReorderChapters={handleReorderChapters}
          showSectionLockToastRef={internalShowSectionLockToastRef}
          coverKey={coverKey}
          onCoverSectionClick={() => setActiveSidebarSection('cover')}
          activeSection={activeSidebarSection ?? 'chapter'}
          onGenerateToc={() => void handleGenerateToc()}
          onGenerateIndex={() => void handleGenerateIndex()}
          tocGenerating={tocGenerating}
          indexGenerating={indexGenerating}
          tocStale={tocStale}
          indexStale={indexStale}
        />
      </aside>

      {/* Center pane — Read tab on mobile, always visible on desktop */}
      <main
        id="studio-content-pane"
        role="tabpanel"
        aria-labelledby="tab-read"
        className={`overflow-y-auto bg-white pb-14 md:pb-0 ${activeMobileTab === 'read' ? 'flex flex-col flex-1' : 'hidden'} md:flex md:flex-col`}
      >
        {/* Section visibility controls — horizontally scrollable on mobile */}
        <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-4 py-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[10px] uppercase tracking-wide text-stone-400 shrink-0">Sections:</span>
          {SECTION_TOGGLES.map(({ type, label }) => {
            const info = sectionState[type];
            if (!info.exists && type !== 'dedication-page') return null;
            return info.exists ? (
              <label key={type} className="flex items-center gap-1.5 cursor-pointer select-none shrink-0">
                <input
                  type="checkbox"
                  checked={info.visible}
                  onChange={e => void toggleSection(type, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-stone-600">{label}</span>
              </label>
            ) : (
              <button
                key={type}
                type="button"
                onClick={() => void toggleSection(type, true)}
                className="shrink-0 text-amber-700 text-xs hover:underline"
              >
                + Add {label}
              </button>
            );
          })}
          {MATTER_TOGGLES.map(({ type, label, chapterIndex }) => {
            const entry = chapters.find(c => c.chapterIndex === chapterIndex && c.areaId === '');
            if (!entry?.sectionId) return null;
            const matterVisible = matterSectionVisible[entry.sectionId] ?? true;
            return (
              <label key={type} className="flex items-center gap-1.5 cursor-pointer select-none shrink-0">
                <input
                  type="checkbox"
                  checked={matterVisible}
                  onChange={e => void toggleMatterSection(chapterIndex, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="text-stone-600">{label}</span>
              </label>
            );
          })}
        </div>

        {activeSidebarSection === 'cover' ? (
          <CoverSection
            restUrl={restUrl}
            coverKey={coverKey}
            bookTitle={bookTitle}
            authorName={authorName}
            onCoverUpdated={(key) => {
              setCoverKey(key);
              setActiveSidebarSection(null);
            }}
          />
        ) : (
          <StudioContent
            bookHtml={bookHtml}
            restUrl={restUrl}
            chapters={chapters}
            lockMap={lockMap}
            onSave={onSave}
            onSelectionChange={handleSelectionChange}
            onChaptersChange={setChapters}
            onLockMapChange={setLockMap}
            acceptRewriteRef={acceptRewriteRef}
            onChapterBodySaved={handleChapterBodySaved}
          />
        )}
      </main>

      {/* Right pane — AI Chat tab on mobile, always visible on desktop */}
      <aside
        id="studio-chat-pane"
        role="tabpanel"
        aria-labelledby="tab-chat"
        className={`bg-white border-l border-stone-200 flex-col ${
          activeMobileTab === 'chat' ? 'flex flex-1' : 'hidden'
        } md:flex`}
        aria-label="AI Edit Assistant"
      >
        <StudioChatPanel
          selection={selection}
          lockMap={lockMap}
          restUrl={restUrl}
          onAccept={handleAccept}
        />
      </aside>

      <MobileTabBar
        activeTab={activeMobileTab}
        onTabChange={setActiveMobileTab}
        hasSelection={selection !== null}
      />
    </div>
  );
}
